import {
  Box,
  Button,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem as ChakraMenuItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { startCase } from 'lodash'
import { useRouter } from 'next/router'
import { parseAsStringEnum, useQueryState } from 'nuqs'
import React from 'react'
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiDownloadLine,
  RiFilter3Line,
  RiAlertLine,
} from 'react-icons/ri'
import { getAddress } from 'viem'
import { useErc20TokenContractAddressesQuery } from 'apollo/generated/graphqlClient'
import { CopyToClipboardButton } from 'components/CopyToClipboardButton'
import { CsvDownloadButton } from 'components/CsvDownloadButton'
import { ExternalLink } from 'components/ExternalLink'
import { InfoPopup } from 'components/InfoPopup'
import { Label } from 'components/Label'
import { Link } from 'components/Link'
import { MenuItem } from 'components/MenuItem'
import { Pagination } from 'components/Pagination'
import { Skeleton } from 'components/Skeleton'
import { Table } from 'components/Table'
import {
  DETAIL_CARD_PADDING_X,
  NOT_APPLICABLE,
  PAGE_LENGTH,
  TXN_STATUS_ICONS,
  TXN_STATUS_VARIANTS,
  TIMESTAMP_COLUMN_FORMAT,
  MAX_PAGE_LENGTH,
  TRUNCATED_MOBILE_TEXT_WIDTH,
} from 'constants/common'
import { CSV_TIMESTAMP_FORMAT } from 'constants/dayjs'
import { Routes } from 'constants/routes'
import { env } from 'env.client'
import {
  getTransactions,
  TransactionListItemStatus as TxItemStatus,
  TransactionListItemTxType as TxType,
  useGetTransactions,
} from 'generated/reactQueryClient'
import type { TransactionListItem as TxItem } from 'generated/reactQueryClient'
import { useCursorPagination } from 'hooks/useCursorPagination'
import { useThemeColors } from 'hooks/useThemeColors'
import { useTransactionHashTruncateOptions } from 'hooks/useTransactionHashTruncateOptions'
import { BORDER_RADIUS_PILL } from 'lib/chakra/constants'
import dayjs from 'lib/dayjs'
import {
  getAssetSymbol,
  getFormattedAsset,
  getFormattedNumber,
  getTruncatedText,
  weiToEth,
} from 'utils/common'
import {
  getTimestampColumnFormatValue,
  getTimestampColumnTooltipFormatValue,
  getTimestampColumnTooltipText,
} from 'utils/dayjs'
import { detectSuspiciousTransactions } from 'utils/detectSuspiciousTransactions'
import { getImageSrc } from 'utils/getImageSrc'

const ARROW_SIZE = 16

const TXN_TYPE_LABELS = {
  [TxType.L1L2]: 'L1 → L2 transactions',
  [TxType.L2]: 'L2 transactions',
  [TxType.L2L1]: 'L2 → L1 transactions',
}

const BATCH_DIRECTIONS = {
  in: 'In',
  out: 'Out',
}

type AddressFromToProps = { txnType: string; hash: string }

const AddressFrom = ({ txnType, hash }: AddressFromToProps) => {
  const { query } = useRouter()
  const TRANSACTION_HASH_TRUNCATE_OPTIONS = useTransactionHashTruncateOptions()
  const truncatedHash = getTruncatedText(hash, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)

  if (txnType === TxType.L1L2) {
    return (
      <Flex alignItems="center" gap={2}>
        <Label variant="primary">L1</Label>
        <ExternalLink
          label={truncatedHash}
          href={`${env.NEXT_PUBLIC_L1_URL}/address/${hash}`}
          onClick={(event) => {
            event.stopPropagation()
          }}
        />
      </Flex>
    )
  }

  if (query.hash !== hash) {
    return (
      <Link
        href={Routes.addressDetail({ hash })}
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {truncatedHash}
      </Link>
    )
  }

  return <Text variant="text3medium">{truncatedHash}</Text>
}

const AddressTo = ({ txnType, hash }: AddressFromToProps) => {
  const { query } = useRouter()
  const TRANSACTION_HASH_TRUNCATE_OPTIONS = useTransactionHashTruncateOptions()
  const truncatedHash = getTruncatedText(hash, ...TRANSACTION_HASH_TRUNCATE_OPTIONS)

  if (txnType === TxType.L2L1) {
    return (
      <Flex alignItems="center" gap={2}>
        <Label variant="primary">L1</Label>
        <ExternalLink
          label={truncatedHash}
          href={`${env.NEXT_PUBLIC_L1_URL}/address/${hash}`}
          onClick={(event) => {
            event.stopPropagation()
          }}
        />
      </Flex>
    )
  }

  if (query.hash !== hash) {
    return (
      <Link
        href={Routes.addressDetail({ hash })}
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {truncatedHash}
      </Link>
    )
  }

  return <Text variant="text3medium">{truncatedHash}</Text>
}

const getBatchVariant = (
  txType: TxType,
  from: string,
  hash: string
): { label: string; variant: 'success' | 'critical' } => {
  if (txType === 'L1L2') {
    return { label: BATCH_DIRECTIONS.in, variant: 'success' }
  }

  if (txType === 'L2L1') {
    return { label: BATCH_DIRECTIONS.out, variant: 'critical' }
  }

  if (from !== hash) {
    return { label: BATCH_DIRECTIONS.in, variant: 'success' }
  }

  return { label: BATCH_DIRECTIONS.out, variant: 'critical' }
}

const getIsActiveMenuItem = (filter: string | null, label: string) =>
  (filter && label === TXN_TYPE_LABELS[filter as TxType]) || (label === 'All' && !filter)

const fetchRecent1000Rows = async (address: string): Promise<TxItem[]> => {
  const fetchBatch = async (accumulated: TxItem[] = [], cursor?: string): Promise<TxItem[]> => {
    const response = await getTransactions({
      limit: MAX_PAGE_LENGTH,
      address,
      ...(cursor && { next: cursor }),
    })

    const newAccumulated = [...accumulated, ...response.data]

    if (newAccumulated.length >= 1000 || !response.paginationContext.next) {
      return newAccumulated
    }

    return fetchBatch(newAccumulated, response.paginationContext.next)
  }

  return fetchBatch()
}

export const Transactions = () => {
  const COLORS = useThemeColors()
  const { cursor, setCursor, direction, setDirection } = useCursorPagination()
  const { query } = useRouter()
  const [filter, setFilter] = useQueryState('filter', parseAsStringEnum(Object.values(TxType)))
  const hash = getAddress(query.hash as string)
  const [isAgeTimestampFormat, setIsAgeTimestampFormat] = React.useState<boolean>(true)
  const downloadMenu = useDisclosure()

  const { data: { data, paginationContext } = { data: [] }, isLoading } = useGetTransactions({
    // +1 Needs to be here for suspicious transactions where edge case could occur for the last transaction
    // if it would happen to be suspicious, there needs to be a check also for the transaction before it.
    limit: PAGE_LENGTH + 1,
    address: hash,
    ...(cursor && { [direction]: cursor }),
    ...(filter && { txType: filter as TxType }),
  })

  const transactions = data.slice(0, PAGE_LENGTH)

  const suspiciousTransactions = React.useMemo(
    () => detectSuspiciousTransactions(data, hash),
    [data, hash]
  )

  const { data: { erc20TokenContractAddresses } = {} } = useErc20TokenContractAddressesQuery()

  const timestampFormat = isAgeTimestampFormat
    ? TIMESTAMP_COLUMN_FORMAT.AGE
    : TIMESTAMP_COLUMN_FORMAT.DATE_TIME

  const getAdditionalContent = () => {
    if (transactions.length === 0) {
      return null
    }

    if (isLoading) {
      return (
        <>
          <Skeleton
            w={{ base: '4.125rem', sm: '6.625rem', md: '7.125rem' }}
            h={{ base: '2rem', md: '2.5rem' }}
            rounded={BORDER_RADIUS_PILL}
          />
          <Skeleton
            w={{ base: '7rem', md: '8rem' }}
            h={{ base: '2rem', md: '2.5rem' }}
            rounded={BORDER_RADIUS_PILL}
          />
        </>
      )
    }

    return (
      <>
        <Menu>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Button}
                variant="secondary"
                borderRadius={BORDER_RADIUS_PILL}
                size={{ base: 'sm', md: 'md' }}
              >
                <Flex gap={1} alignItems="center">
                  <Box color={COLORS.grey02}>
                    <RiFilter3Line size={ARROW_SIZE} />
                  </Box>
                  <Text
                    variant="caption2medium"
                    color={COLORS.grey02}
                    display={{ base: 'none', sm: 'block' }}
                  >
                    Filters:
                  </Text>
                  <Text variant="caption2medium">
                    {(Boolean(filter) && TXN_TYPE_LABELS[filter as TxType]) ?? filter ?? 'All'}
                  </Text>
                  <Box color={COLORS.dark01}>
                    {isOpen ? (
                      <RiArrowUpSLine size={ARROW_SIZE} />
                    ) : (
                      <RiArrowDownSLine size={ARROW_SIZE} />
                    )}
                  </Box>
                </Flex>
              </MenuButton>
              <MenuList>
                <MenuItem
                  isActive={getIsActiveMenuItem(filter, 'All')}
                  label="All"
                  onClick={async () => {
                    await setFilter(null)
                  }}
                />
                <MenuItem
                  isActive={getIsActiveMenuItem(filter, TXN_TYPE_LABELS.L2)}
                  label={TXN_TYPE_LABELS.L2}
                  onClick={async () => {
                    await setFilter(TxType.L2)
                  }}
                />
                <MenuItem
                  isActive={getIsActiveMenuItem(filter, TXN_TYPE_LABELS.L1L2)}
                  label={TXN_TYPE_LABELS.L1L2}
                  onClick={async () => {
                    await setFilter(TxType.L1L2)
                  }}
                />
                <MenuItem
                  isActive={getIsActiveMenuItem(filter, TXN_TYPE_LABELS.L2L1)}
                  label={TXN_TYPE_LABELS.L2L1}
                  onClick={async () => {
                    await setFilter(TxType.L2L1)
                  }}
                />
              </MenuList>
            </>
          )}
        </Menu>
        <Menu {...downloadMenu} closeOnSelect={false} autoSelect={false}>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Button}
                variant="secondary"
                borderRadius={BORDER_RADIUS_PILL}
                size={{ base: 'sm', md: 'md' }}
              >
                <Flex gap={1} alignItems="center">
                  <Box color={COLORS.grey02}>
                    <RiDownloadLine size={ARROW_SIZE} />
                  </Box>
                  <Text variant="caption2medium" color={COLORS.grey02}>
                    Download
                  </Text>
                  <Box color={COLORS.dark01}>
                    {isOpen ? (
                      <RiArrowUpSLine size={ARROW_SIZE} />
                    ) : (
                      <RiArrowDownSLine size={ARROW_SIZE} />
                    )}
                  </Box>
                </Flex>
              </MenuButton>
              <MenuList maxW="12rem">
                <CsvDownloadButton
                  as={ChakraMenuItem}
                  data={transactions}
                  filename={`transactions-${hash}-current-page-${new Date().toISOString()}.csv`}
                  columns={[
                    { property: 'l2TxHash', label: 'Txn Hash' },
                    { property: 'l2BlockIndex', label: 'Block L2' },
                    { property: 'l1BlockIndex', label: 'Block L1' },
                    {
                      property: 'timestamp',
                      label: 'Date Time (UTC)',
                      formatter: (value) => dayjs.unix(value).utc().format(CSV_TIMESTAMP_FORMAT),
                    },
                    { property: 'from', label: 'From' },
                    { property: 'to', label: 'To' },
                    {
                      property: 'status',
                      label: 'Txn Status',
                      formatter: (value, row) =>
                        `${startCase(value)}${
                          value === TxItemStatus.failed ? ` (${row.failureReason})` : ''
                        }`,
                    },
                    {
                      property: 'value',
                      label: 'Value',
                      formatter: (value, row) =>
                        getFormattedAsset({ transfers: row.transfers, value }).replaceAll(',', ''), // Remove commas from the localized number
                    },
                    {
                      property: 'fee',
                      label: 'Txn Fee',
                      formatter: (value) =>
                        `${getFormattedNumber(weiToEth(value)).replaceAll(',', '')} ETH`, // Remove commas from the localized number
                    },
                  ]}
                  onDownloadComplete={downloadMenu.onClose}
                  hasIcon={false}
                  variant="menuItem"
                >
                  <Text variant="caption2medium" w="full" textAlign="left">
                    Current Page
                  </Text>
                </CsvDownloadButton>
                <CsvDownloadButton
                  as={ChakraMenuItem}
                  fetchData={() => fetchRecent1000Rows(hash)}
                  filename={`transactions-${hash}-recent-1000-${new Date().toISOString()}.csv`}
                  columns={[
                    { property: 'l2TxHash', label: 'Txn Hash' },
                    { property: 'l2BlockIndex', label: 'Block L2' },
                    { property: 'l1BlockIndex', label: 'Block L1' },
                    {
                      property: 'timestamp',
                      label: 'Date Time (UTC)',
                      formatter: (value) => dayjs.unix(value).utc().format(CSV_TIMESTAMP_FORMAT),
                    },
                    { property: 'from', label: 'From' },
                    { property: 'to', label: 'To' },
                    {
                      property: 'status',
                      label: 'Txn Status',
                      formatter: (value, row) =>
                        `${startCase(value)}${
                          value === TxItemStatus.failed ? ` (${row.failureReason})` : ''
                        }`,
                    },
                    {
                      property: 'value',
                      label: 'Value',
                      formatter: (value, row) =>
                        getFormattedAsset({ transfers: row.transfers, value }).replaceAll(',', ''), // Remove commas from the localized number
                    },
                    {
                      property: 'fee',
                      label: 'Txn Fee',
                      formatter: (value) =>
                        `${getFormattedNumber(weiToEth(value)).replaceAll(',', '')} ETH`, // Remove commas from the localized number
                    },
                  ]}
                  onDownloadComplete={downloadMenu.onClose}
                  hasIcon={false}
                  variant="menuItem"
                >
                  <Text variant="caption2medium" w="full" textAlign="left">
                    Recent 1000 transactions
                  </Text>
                </CsvDownloadButton>
              </MenuList>
            </>
          )}
        </Menu>
      </>
    )
  }

  return (
    <Box mx={-DETAIL_CARD_PADDING_X}>
      <Flex
        flexDirection={{ base: 'column', md: 'row' }}
        px={5}
        py={2}
        bgColor={COLORS.grey07}
        borderBottom={{ base: 'none', lg: '1px solid' }}
        borderColor={{ base: 'none', lg: COLORS.grey06 }}
        alignItems={{ base: 'flex-end', md: 'center' }}
        gap={2}
      >
        <Flex
          w="full"
          justifyContent="space-between"
          alignItems={{ base: 'flex-end', md: 'center' }}
          gap={{ base: 3, lg: 6 }}
        >
          {getAdditionalContent()}
        </Flex>
        <Pagination
          isDisabled={isLoading || transactions?.length === 0}
          hasNextPage={!!paginationContext?.next}
          hasPreviousPage={!!paginationContext?.previous}
          onNextPage={() => {
            setDirection('next')
            setCursor(paginationContext?.next ?? '')
          }}
          onPreviousPage={() => {
            setDirection('previous')
            setCursor(paginationContext?.previous ?? '')
          }}
        />
      </Flex>
      <Table
        variant="secondary"
        data={transactions}
        isLoading={isLoading}
        hasTopContent={false}
        withTopRadius={false}
        renderMobileRow={(row: TxItem) => {
          const { label, variant } = getBatchVariant(row.txType, row.from, hash)
          const erc20TokenAsset = erc20TokenContractAddresses?.find(
            ({ symbol }: { symbol: string }) =>
              symbol === getAssetSymbol(row?.transfers?.[0]?.symbol)
          )

          const isSuspicious = suspiciousTransactions.some((tx) => tx.l2TxHash === row.l2TxHash)

          return (
            <Table.MobileRow key={row.l2TxHash}>
              {({ isExpanded }) => (
                <Flex flex="1" flexDirection="column" textAlign="left" gap={3}>
                  <Table.MobileRowCell label="Txn Hash:">
                    <Link
                      href={Routes.transactionDetail({ txHash: row.l2TxHash, activeTab: 0 })}
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      w={TRUNCATED_MOBILE_TEXT_WIDTH}
                      isTruncated
                    >
                      {row.l2TxHash}
                    </Link>
                  </Table.MobileRowCell>
                  <Table.AnimatedMobileRowCell label="Block L2:" isVisible={isExpanded}>
                    <Link
                      href={Routes.blockDetail({ id: String(row.l2BlockIndex) })}
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                    >
                      {row.l2BlockIndex}
                    </Link>
                  </Table.AnimatedMobileRowCell>
                  <Table.AnimatedMobileRowCell label="Block L1:" isVisible={isExpanded}>
                    <Box>
                      {row.l1BlockIndex ? (
                        <ExternalLink
                          href={`${env.NEXT_PUBLIC_L1_URL}/block/${String(row.l1BlockIndex)}`}
                          label={String(row.l1BlockIndex)}
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                        />
                      ) : (
                        <Text variant="text3regular">{NOT_APPLICABLE}</Text>
                      )}
                    </Box>
                  </Table.AnimatedMobileRowCell>
                  <Table.AnimatedMobileRowCell
                    label={
                      transactions?.some((tx) => tx.timestamp) ? (
                        <Table.InteractiveMobileHeaderCell
                          variant="text3regular"
                          onClick={() => {
                            setIsAgeTimestampFormat((prev) => !prev)
                          }}
                        >
                          {timestampFormat}:
                        </Table.InteractiveMobileHeaderCell>
                      ) : (
                        `${timestampFormat}:`
                      )
                    }
                    isVisible={isExpanded}
                  >
                    <InfoPopup
                      title={
                        timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                          ? 'Transaction Datetime:'
                          : 'Transaction Age:'
                      }
                      triggerElement={
                        <Text
                          variant="text3regular"
                          color={COLORS.grey03}
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          {row.timestamp
                            ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                            : NOT_APPLICABLE}
                        </Text>
                      }
                    >
                      {row.timestamp
                        ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                        : NOT_APPLICABLE}
                    </InfoPopup>
                  </Table.AnimatedMobileRowCell>
                  <Table.MobileRowCell label="From:">
                    <Flex alignItems="center" gap={1}>
                      {row.from ? (
                        <AddressFrom txnType={row.txType} hash={row.from} />
                      ) : (
                        <Text variant="text3regular" color={COLORS.grey03}>
                          {NOT_APPLICABLE}
                        </Text>
                      )}
                      {isSuspicious && (
                        <InfoPopup
                          title="Suspicious Activity Detected"
                          triggerElement={<RiAlertLine size={16} color="red" />}
                        >
                          <Text variant="text3regular">
                            This address shows signs of a dusting or phishing attempt based on
                            repeated small transactions in a short timeframe.
                          </Text>
                        </InfoPopup>
                      )}
                    </Flex>
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label={undefined}>
                    <Label variant={variant}>{label}</Label>
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label="To:">
                    {row.to ? (
                      <AddressTo txnType={row.txType} hash={row.to} />
                    ) : (
                      <Text variant="text3regular" color={COLORS.grey03}>
                        {NOT_APPLICABLE}
                      </Text>
                    )}
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label="Txn Status:">
                    {row.status ? (
                      <Box>
                        {row.status === TxItemStatus.failed ? (
                          <InfoPopup
                            title="Txn Status:"
                            triggerElement={
                              <Label
                                variant={TXN_STATUS_VARIANTS[row.status]}
                                icon={TXN_STATUS_ICONS[row.status]}
                                onClick={(event) => {
                                  event.stopPropagation()
                                }}
                              >
                                {row.status}
                              </Label>
                            }
                          >
                            {row.failureReason}
                          </InfoPopup>
                        ) : (
                          <Label
                            variant={TXN_STATUS_VARIANTS[row.status]}
                            icon={TXN_STATUS_ICONS[row.status]}
                          >
                            {row.status}
                          </Label>
                        )}
                      </Box>
                    ) : (
                      <Text variant="text3regular" color={COLORS.grey03}>
                        {NOT_APPLICABLE}
                      </Text>
                    )}
                  </Table.MobileRowCell>
                  <Table.MobileRowCell label="Value:">
                    <Flex alignItems="center">
                      <Image
                        alt="Token Asset"
                        {...(row.transfers?.[0]?.symbol &&
                        erc20TokenContractAddresses?.length &&
                        erc20TokenAsset?.type
                          ? {
                              src: getImageSrc({
                                contractAddressL2: erc20TokenAsset?.contract_address_l2,
                                type: erc20TokenAsset.type,
                              }),
                              boxSize: 4,
                            }
                          : { src: '/assets/ethereum2.svg' })}
                      />
                      <Text ml={1} variant="text3regular" whiteSpace="nowrap">
                        {getFormattedAsset({ transfers: row.transfers, value: row.value })}
                      </Text>
                    </Flex>
                  </Table.MobileRowCell>
                  <Table.AnimatedMobileRowCell label="Txn Fee:" isVisible={isExpanded}>
                    <Text
                      variant="text3regular"
                      color={COLORS.grey03}
                      w={TRUNCATED_MOBILE_TEXT_WIDTH}
                      isTruncated
                      whiteSpace="nowrap"
                    >
                      {getFormattedNumber(weiToEth(row.fee))} ETH
                    </Text>
                  </Table.AnimatedMobileRowCell>
                </Flex>
              )}
            </Table.MobileRow>
          )
        }}
        renderTableHeader={() => (
          <>
            <Table.HeaderCell>Txn Hash</Table.HeaderCell>
            <Table.HeaderCell>Block L2</Table.HeaderCell>
            <Table.HeaderCell>Block L1</Table.HeaderCell>
            {transactions?.some((tx) => tx.timestamp) ? (
              <Table.InteractiveHeaderCell
                tooltipText={getTimestampColumnTooltipText(timestampFormat)}
                onClick={() => {
                  setIsAgeTimestampFormat((prev) => !prev)
                }}
              >
                {timestampFormat}
              </Table.InteractiveHeaderCell>
            ) : (
              <Table.HeaderCell>{timestampFormat}</Table.HeaderCell>
            )}
            <Table.HeaderCell>From</Table.HeaderCell>
            <Table.HeaderCell />
            <Table.HeaderCell>To</Table.HeaderCell>
            <Table.HeaderCell>Txn Status</Table.HeaderCell>
            <Table.HeaderCell>Value</Table.HeaderCell>
            <Table.HeaderCell>Txn Fee</Table.HeaderCell>
          </>
        )}
        renderTableRow={(row: TxItem) => {
          const { label, variant } = getBatchVariant(row.txType, row.from, hash)

          const erc20TokenAsset = erc20TokenContractAddresses?.find(
            ({ symbol }: { symbol: string }) =>
              symbol === getAssetSymbol(row?.transfers?.[0]?.symbol)
          )

          const isSuspicious = suspiciousTransactions.some((tx) => tx.l2TxHash === row.l2TxHash)

          return (
            <Table.Row key={row.l2TxHash}>
              <Table.RowCell>
                <Flex alignItems="center" gap={1}>
                  <Link
                    href={Routes.transactionDetail({ txHash: row.l2TxHash, activeTab: 0 })}
                    maxW={180}
                    isTruncated
                  >
                    {row.l2TxHash}
                  </Link>
                  <CopyToClipboardButton text={row.l2TxHash} />
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <Link href={Routes.blockDetail({ id: String(row.l2BlockIndex) })}>
                  {row.l2BlockIndex}
                </Link>
              </Table.RowCell>
              <Table.RowCell>
                {row.l1BlockIndex ? (
                  <ExternalLink
                    href={`${env.NEXT_PUBLIC_L1_URL}/block/${String(row.l1BlockIndex)}`}
                    label={String(row.l1BlockIndex)}
                  />
                ) : (
                  <Text variant="text3regular">{NOT_APPLICABLE}</Text>
                )}
              </Table.RowCell>
              <Table.RowCell>
                <InfoPopup
                  title={
                    timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                      ? 'Transaction Datetime:'
                      : 'Transaction Age:'
                  }
                  triggerElement={
                    <Text variant="text3regular" color={COLORS.grey03}>
                      {row.timestamp
                        ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                        : NOT_APPLICABLE}
                    </Text>
                  }
                >
                  {row.timestamp
                    ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                    : NOT_APPLICABLE}
                </InfoPopup>
              </Table.RowCell>
              {row.from ? (
                <Table.RowCell>
                  <Flex alignItems="center" gap={1}>
                    <AddressFrom txnType={row.txType} hash={row.from} />
                    <CopyToClipboardButton text={row.from} />
                    {isSuspicious && (
                      <InfoPopup
                        title="Txn Status:"
                        triggerElement={<RiAlertLine size={16} color="red" />}
                      >
                        <Text variant="text3medium">Suspicious Activity Detected</Text>
                        <Text variant="text3regular">
                          This address shows signs of a dusting or phishing attempt based on
                          repeated small transactions in a short timeframe.
                        </Text>
                      </InfoPopup>
                    )}
                  </Flex>
                </Table.RowCell>
              ) : (
                <Table.RowCell>
                  <Text variant="text3regular" color={COLORS.grey03}>
                    {NOT_APPLICABLE}
                  </Text>
                </Table.RowCell>
              )}
              <Table.RowCell>
                <Label variant={variant}>{label}</Label>
              </Table.RowCell>
              {row.to ? (
                <Table.RowCell>
                  <Flex alignItems="center" gap={1}>
                    <AddressTo txnType={row.txType} hash={row.to} />
                    <CopyToClipboardButton text={row.to} />
                  </Flex>
                </Table.RowCell>
              ) : (
                <Table.RowCell>
                  <Text variant="text3regular" color={COLORS.grey03}>
                    {NOT_APPLICABLE}
                  </Text>
                </Table.RowCell>
              )}
              {row.status ? (
                <Table.RowCell>
                  {row.status === TxItemStatus.failed ? (
                    <InfoPopup
                      title="Txn Status:"
                      triggerElement={
                        <Label
                          variant={TXN_STATUS_VARIANTS[row.status]}
                          icon={TXN_STATUS_ICONS[row.status]}
                        >
                          {row.status}
                        </Label>
                      }
                    >
                      {row.failureReason}
                    </InfoPopup>
                  ) : (
                    <Label
                      variant={TXN_STATUS_VARIANTS[row.status]}
                      icon={TXN_STATUS_ICONS[row.status]}
                    >
                      {row.status}
                    </Label>
                  )}
                </Table.RowCell>
              ) : (
                <Table.RowCell>
                  <Text variant="text3regular" color={COLORS.grey03}>
                    {NOT_APPLICABLE}
                  </Text>
                </Table.RowCell>
              )}
              <Table.RowCell>
                <Flex alignItems="center">
                  <Image
                    alt="Token Asset"
                    {...(row.transfers?.[0]?.symbol &&
                    erc20TokenContractAddresses?.length &&
                    erc20TokenAsset?.type
                      ? {
                          src: getImageSrc({
                            contractAddressL2: erc20TokenAsset?.contract_address_l2,
                            type: erc20TokenAsset.type,
                          }),
                          boxSize: 4,
                        }
                      : { src: '/assets/ethereum2.svg' })}
                  />
                  <Text ml={1} variant="text3regular">
                    {getFormattedAsset({ transfers: row.transfers, value: row.value })}
                  </Text>
                </Flex>
              </Table.RowCell>
              <Table.RowCell>
                <Text variant="text3regular" color={COLORS.grey03}>
                  {getFormattedNumber(weiToEth(row.fee))} ETH
                </Text>
              </Table.RowCell>
            </Table.Row>
          )
        }}
      />
    </Box>
  )
}
